import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'aboutUs', href: '#about' },
    { key: 'products', href: '#products' },
    { key: 'careers', href: '#careers' },
    { key: 'distributorEnquiry', href: '#distributor' },
    { key: 'salesmanApplication', href: '#salesman' },
    { key: 'contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'header-glass shadow-md' : 'bg-white'
      }`}
      data-testid="header"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <a href="#home" onClick={(e) => scrollToSection(e, '#home')} className="flex-shrink-0 flex items-center gap-2" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_gupta-grih-udyog/artifacts/rao6ba83_generated-image.png" 
              alt="Gupta Soap Logo" 
              className="h-10 md:h-12 w-auto"
            />
            <div className="logo-text hidden sm:block">
              <span className="text-base md:text-lg font-black text-[#0047AB] tracking-tight leading-tight">Gupta Soap Grih Udyog</span>
              <span className="block text-[10px] font-medium text-slate-500 tracking-wide">Since 1973</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-4" data-testid="desktop-nav">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="nav-link text-xs font-medium whitespace-nowrap"
                data-testid={`nav-${item.key}`}
              >
                {t(item.key)}
              </a>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <div className="lang-toggle hidden md:flex text-xs" data-testid="language-toggle">
              <button 
                className={language === 'en' ? 'active' : ''} 
                onClick={() => setLanguage('en')}
                data-testid="lang-en"
              >
                EN
              </button>
              <span className="text-slate-400">|</span>
              <button 
                className={language === 'hi' ? 'active' : ''} 
                onClick={() => setLanguage('hi')}
                data-testid="lang-hi"
              >
                हिंदी
              </button>
            </div>

            {/* Call Button */}
            <a href="tel:9919091190" data-testid="call-button">
              <Button variant="outline" size="sm" className="hidden lg:flex items-center gap-1 border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB] hover:text-white text-xs px-3 py-1.5">
                <Phone className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">{t('callNow')}</span>
              </Button>
            </a>

            {/* WhatsApp Button */}
            <a 
              href="https://wa.me/919919091190" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="whatsapp-header-button"
            >
              <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-1 text-xs px-3 py-1.5">
                <MessageCircle className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">WhatsApp</span>
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <button 
              className="xl:hidden p-2" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden border-t border-slate-200 py-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-[#0047AB]"
                >
                  {t(item.key)}
                </a>
              ))}
              {/* Mobile Language Toggle */}
              <div className="px-4 py-2 flex items-center gap-2 border-t border-slate-200 mt-2 pt-4">
                <button 
                  className={`px-3 py-1 text-sm ${language === 'en' ? 'bg-[#0047AB] text-white' : 'text-slate-600'}`}
                  onClick={() => setLanguage('en')}
                >
                  English
                </button>
                <button 
                  className={`px-3 py-1 text-sm ${language === 'hi' ? 'bg-[#0047AB] text-white' : 'text-slate-600'}`}
                  onClick={() => setLanguage('hi')}
                >
                  हिंदी
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
